import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { UserService } from "../../core/userService/UserService";
import { PartnershipService } from "../../core/partnershipService/PartnershipService";
import { CoreError, CoreErrorCause } from "../models/CoreError";
import { AuthUser } from "../models/AuthUser";
import { SessionTeardownService } from "../../core/sessionTeardownService/SessionTeardownService";
import { NotificationsFeature } from "../notifications/notificationsFeature";

/** Push cleanup is best-effort; sign-out never waits on it longer than this. */
const SIGN_OUT_PUSH_CLEANUP_TIMEOUT_MS = 3000;

/**
 * Orchestrates authentication-related operations.
 * This feature coordinates between AuthenticationService, UserService, and PartnershipService
 * to provide complete authentication flows like signup.
 *
 * Currently supports:
 * - Signup (creates auth user, user profile, and partnership)
 * - Login (authenticates user)
 * - Reset password (sends password reset email)
 *
 * Future additions can include:
 * - Other authentication-related operations
 */
export class AuthFeature {
  /**
   * Returns the currently authenticated user, if any.
   */
  public static getAuthUser(): AuthUser | null {
    return AuthenticationService.getInstance().authUser;
  }

  /**
   * Signs up a new user with email and password.
   * This method performs the complete signup flow:
   * 1. Creates a Firebase Auth user
   * 2. Creates a user profile in the database
   * 3. Creates a pending partnership with an invitation code
   *
   * @param email The user's email address
   * @param password The user's password
   * @returns Promise that resolves when signup is complete
   * @throws CoreError if any step fails
   */
  public static async signup(email: string, password: string): Promise<void> {
    const authService = AuthenticationService.getInstance();
    const userService = UserService.getInstance();
    const partnershipService = PartnershipService.getInstance();

    try {
      SessionTeardownService.reset();
      // Step 1: Create Firebase Auth user
      const authUser = await authService.signUp(email, password);

      if (!authUser.id) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: "Sign-up failed: User identifier not returned",
        });
      }

      // Step 2: Create user profile in database
      await userService.createUser(authUser.id, email);

      // Step 3: Create partnership (without invitation code, it will be generated)
      await partnershipService.createPartnership(authUser.id);
    } catch (error) {
      // If user creation or partnership creation fails, we should clean up
      // the auth user to prevent orphaned accounts. However, for now we'll
      // just rethrow the error and let the caller handle it.
      // TODO: Consider implementing cleanup logic for failed signup attempts
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Sign-up failed",
        underlyingException: error,
      });
    }
  }

  /**
   * Signs in an existing user with email and password.
   * This method performs the complete login flow:
   * 1. Authenticates the user with Firebase Auth
   * 2. Fetches initial data (constants, etc.)
   *
   * @param email The user's email address
   * @param password The user's password
   * @returns Promise that resolves when login is complete
   * @throws CoreError if any step fails
   */
  public static async login(email: string, password: string): Promise<void> {
    const authService = AuthenticationService.getInstance();

    try {
      SessionTeardownService.reset();
      // Step 1: Authenticate user with Firebase Auth
      const authUser = await authService.signIn(email, password);

      if (!authUser.id) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: "Login failed: User identifier not returned",
        });
      }
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Login failed",
        underlyingException: error,
      });
    }
  }

  /**
   * Sends a password reset email to the specified email address.
   *
   * @param email The email address to send the reset link to
   * @returns Promise that resolves when the reset email is sent
   * @throws CoreError if the operation fails
   */
  public static async resetPassword(email: string): Promise<void> {
    const authService = AuthenticationService.getInstance();

    try {
      await authService.resetPassword(email);
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Password reset failed",
        underlyingException: error,
      });
    }
  }

  /**
   * Signs out the current user.
   */
  public static async signOut(): Promise<void> {
    const authService = AuthenticationService.getInstance();

    try {
      // Must run before the auth session ends (RTDB rules) — removes this
      // browser's push token so the next account here never gets our pushes.
      // Raced with a timeout: the cleanup awaits a dynamic firebase/messaging
      // chunk import, and a hung network must not hang sign-out.
      await Promise.race([
        NotificationsFeature.stopForAuthenticatedUser(),
        new Promise<void>((resolve) => {
          setTimeout(resolve, SIGN_OUT_PUSH_CLEANUP_TIMEOUT_MS);
        }),
      ]);
      SessionTeardownService.teardownAll();
      await authService.signOut();
    } catch (error) {
      SessionTeardownService.reset();
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Sign-out failed",
        underlyingException: error,
      });
    }
  }

  /**
   * Re-authenticates the current user with their password.
   */
  public static async reauthenticate(currentPassword: string): Promise<void> {
    const authService = AuthenticationService.getInstance();

    try {
      await authService.reauthenticateUser(currentPassword);
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Re-authentication failed",
        underlyingException: error,
      });
    }
  }

  /**
   * Detaches every session listener ahead of account deletion. The backend
   * delete callable wipes users/<uid> and removes the auth user; without this,
   * still-attached RTDB observers fire DataNotFound and permission-denied
   * while the deletion is in flight.
   */
  public static prepareForAccountDeletion(): void {
    SessionTeardownService.teardownAll();
  }

  /**
   * Deletes the currently authenticated account from auth provider.
   */
  public static async deleteAccount(): Promise<void> {
    const authService = AuthenticationService.getInstance();

    try {
      await authService.deleteAccount();
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: "Account deletion failed",
        underlyingException: error,
      });
    }
  }
}
